import type { RioTripsFilters, RioTripsPage } from "@types";
import {
  IBranchResponse,
  ICreateBranchPayload,
  ISupplierBody,
  ICategoryResponse,
  ITransactionPayload,
  ISupplyTransactionPayload,
  ISegmentResponse,
  ISubcategoryResponse,
  IClientResponse,
  IRecurrenceGroupDeletePayload,
  IRecurrenceGroupUpdatePayload,
} from "@types";
import api, { v2BaseUrl } from "../api";
import { buildAssetTreePayload } from "@utils/assetTreePayload";

type ApiResponse = {
  data?: any;
  content?: any;
  items?: any;
};

export const unwrapList = (response: ApiResponse): any[] => {
  const payload = response?.data ?? response;

  if (Array.isArray(payload)) {
    return payload;
  }

  if (Array.isArray(response?.content)) {
    return response.content;
  }

  if (Array.isArray(response?.items)) {
    return response.items;
  }

  if (payload && typeof payload === "object") {
    if (Array.isArray(payload.data)) {
      return payload.data;
    }
    if (Array.isArray(payload.content)) {
      return payload.content;
    }
    if (Array.isArray(payload.items)) {
      return payload.items;
    }
  }

  return [];
};

export const getBranch = async (): Promise<IBranchResponse[]> => {
  const response = await api.get("/branch?includeInactive=true");

  return unwrapList(response);
};

export const getActiveBranches = async (): Promise<IBranchResponse[]> => {
  const response = await api.get("/branch?active=true");

  return unwrapList(response);
};

export const createBranch = async (payload: ICreateBranchPayload) => {
  const response = await api.post("/branch", payload);

  return response.data;
};

export const createBranches = async (branches: Array<{ name: string }>) => {
  const response = await api.post("/branch/branches", branches);

  return response.data;
};

export const editBranch = async (payload: ICreateBranchPayload) => {
  const response = await api.post(`/branch`, payload);

  return response.data;
};

export const deleteBranch = async (id: number) => {
  const response = await api.delete(`/branch/${id}`);

  return response.data;
};

export const inactivateBranch = async (id: number, activate: boolean) => {
  const response = await api.patch(`/branch/${id}/soft-delete`, { activate });

  return response.data;
};

export const getTransactions = async () => {
  const response = await api.get("/transactions");

  return response.data;
};

export const createTransaction = async (payload: ITransactionPayload, file?: File | null) => {
  const formData = new FormData();

  formData.append("transaction", JSON.stringify(payload));

  if (file) {
    formData.append("file", file);
  }

  const response = await api.post("/transactions", formData);

  return response.data;
};

export const createSupplyTransaction = async (payload: ISupplyTransactionPayload, file?: File | null) => {
  if (!file) {
    const response = await api.post("/transactions/supply", payload);
    return response.data;
  }

  const formData = new FormData();
  formData.append("transaction", JSON.stringify(payload));
  formData.append("file", file);

  const response = await api.post("/transactions/supply", formData);

  return response.data;
};

export const createOdometerTransaction = async (transactionId: string, file?: File | null) => {
  if (!file) {
    return;
  }

  const formData = new FormData();
  formData.append("file", file);

  const response = await api.post(`/fuel-refills/${transactionId}/odometer-photos`, formData);

  return response.data;
};

export const putOdometerTransaction = async (
  transactionId: string,
  file?: File | null,
  idOdometerPhoto?: number | null
) => {
  if (!file) {
    return;
  }

  const formData = new FormData();
  formData.append("file", file);

  const response = await api.put(`/fuel-refills/${transactionId}/odometer-photos/${idOdometerPhoto}`, formData);

  return response.data;
};

export const getOdometerTransaction = async (transactionId?: string | null) => {
  if (!transactionId) {
    return;
  }

  const response = await api.get(`/fuel-refills/${transactionId}/odometer-photos`);

  return response.data;
};

export const deleteOdometerTransaction = async (transactionId: string, photoId: number) => {
  const response = await api.delete(`/fuel-refills/${transactionId}/odometer-photos/${photoId}`);

  return response.data;
};

export const editTransaction = async (id: string, payload: ITransactionPayload, file?: File | null) => {
  if (!file) {
    const response = await api.put(`/transactions/${id}`, payload);
    return response.data;
  }

  const formData = new FormData();

  formData.append("transaction", JSON.stringify(payload));
  formData.append("file", file);

  const response = await api.put(`/transactions/${id}`, formData);

  return response.data;
};

export const getSegments = async (): Promise<ISegmentResponse[]> => {
  const response = await api.get("/operations?includeInactive=true");

  return unwrapList(response);
};

export const getActiveSegments = async (): Promise<ISegmentResponse[]> => {
  const response = await api.get("/operations?active=true");

  return unwrapList(response);
};

export const createSegments = async (value: string) => {
  const response = await api.post("/operations", { name: value });

  return response.data;
};

export const editSegments = async (payload: ICreateBranchPayload) => {
  const response = await api.post(`/operations`, payload);

  return response.data;
};

export const deleteSegments = async (id: number) => {
  const response = await api.delete(`/operations/${id}`);

  return response.data;
};

export const inactivateSegments = async (id: number, activate: boolean) => {
  const response = await api.patch(`/operations/${id}/soft-delete`, { activate });

  return response.data;
};

export const getCategories = async (): Promise<ICategoryResponse[]> => {
  const response = await api.get("/category?includeInactive=true");

  return unwrapList(response);
};

export const getActiveCategories = async (): Promise<ICategoryResponse[]> => {
  const response = await api.get("/category?active=true");

  return unwrapList(response);
};

type CategoryType = { type?: "RECEITA" | "DESPESA" };
type CategoryExpenseType = { expenseType?: "DIRETO" | "OPERACIONAL" };

type CreateCategory = ICreateBranchPayload & CategoryType & CategoryExpenseType;

const postCategory = async (payload: CreateCategory) => {
  const response = await api.post("/category", payload);

  return response.data;
};

export const createCategory = async (payload: CreateCategory) => postCategory(payload);

export const editCategory = async (payload: CreateCategory) => postCategory(payload);

export const deleteCategory = async (id: number) => {
  const response = await api.delete(`/category/${id}`);

  return response.data;
};

export const inactivateCategory = async (id: number, activate: boolean) => {
  const response = await api.patch(`/category/${id}/soft-delete`, { activate });

  return response.data;
};

export const createSubCategory = async (payload: ICreateBranchPayload) => postCategory(payload);

export const getSubCategories = async (id: string) => {
  const response = await api.get(`/subcategory?categoryId=${id}&includeInactive=true`);

  return response.data;
};

export const getActiveSubCategories = async (id: string) => {
  const response = await api.get(`/subcategory?categoryId=${id}&active=true`);

  return response.data;
};

export const getAllSubCategories = async (): Promise<ISubcategoryResponse[]> => {
  const response = await api.get("/subcategory?includeInactive=true");

  return unwrapList(response);
};

const postSubCategory = async (id: string, payload: ICreateBranchPayload) => {
  const response = await api.post(`/subcategory`, {
    ...payload,
    displayOrder: 0,
    active: true,
    categoryId: id,
  });

  return response.data;
};

export const createSubCategories = async (id: string, payload: ICreateBranchPayload) => postSubCategory(id, payload);

export const editSubCategories = async (id: string, payload: ICreateBranchPayload) => postSubCategory(id, payload);

export const deleteSubCategories = async (id: number) => {
  const response = await api.delete(`/subcategory/${id}`);

  return response.data;
};

export const inactivateSubCategories = async (id: number, activate: boolean) => {
  const response = await api.patch(`/subcategory/${id}/soft-delete`, { activate });

  return response.data;
};

export const getAccounts = async () => {
  const response = await api.get("/rio/accounts");

  return unwrapList(response);
};

export const getSuppliers = async (type?: string): Promise<IClientResponse[]> => {
  const base = type ? `/supplier-client?type=${type}` : "/supplier-client";
  const url = `${base}${base.includes("?") ? "&" : "?"}includeInactive=true`;
  const response = await api.get(url);

  return unwrapList(response);
};

export const getActiveSuppliers = async (type?: string): Promise<IClientResponse[]> => {
  const base = type ? `/supplier-client?type=${type}` : "/supplier-client";
  const url = `${base}${base.includes("?") ? "&" : "?"}active=true`;
  const response = await api.get(url);

  return unwrapList(response);
};

export const getAllSuppliers = async () => {
  const response = await api.get("/supplier-client");

  return response.data;
};

export const createSuppliers = async (payload: ISupplierBody) => {
  const response = await api.post("/supplier-client ", payload);

  return response.data;
};

export const editSuppliers = async (payload: ISupplierBody) => {
  const response = await api.post(`/supplier-client`, payload);

  return response.data;
};

export const deleteSuppliers = async (id: number) => {
  const response = await api.delete(`/supplier-client/${id}`);

  return response.data;
};

export const inactivateSuppliers = async (id: number, activate: boolean) => {
  const response = await api.patch(`/supplier-client/${id}/soft-delete`, { activate });

  return response.data;
};

export const createAccounts = async (payload: ICreateBranchPayload) => {
  const response = await api.post("/rio/accounts", payload);

  return response.data;
};

export const getAssets = async () => {
  const response = await api.get("/rio/assets");

  return response.data;
};

export const getFleetGroups = async () => {
  const response = await api.get("/rio/tags");

  return unwrapList(response);
};

/**
 * Busca assets agrupados por frota do novo endpoint.
 * Retorna dados cacheados do banco - usar syncFleetGroups() para atualizar.
 */
export const getFleetGroupsWithAssets = async () => {
  const response = await api.get("/fleet-groups");
  return response.data?.data ?? response.data;
};

/**
 * Sincroniza assets e frotas com a RIO API.
 * Atualiza o cache no banco de dados.
 */
export const syncFleetGroups = async () => {
  const response = await api.post("/fleet-groups/sync");
  return response.data?.data ?? response.data;
};

export const getRioTrips = async (filters?: RioTripsFilters): Promise<RioTripsPage> => {
  const params = Object.fromEntries(
    Object.entries(filters ?? {}).filter(([, valor]) => valor !== undefined && valor !== null && valor !== "")
  );

  const response = await api.get("/rio/trips", { params });
  const data = response.data?.data ?? response.data ?? {};

  return {
    totalElements: data.totalElements ?? 0,
    totalPages: data.totalPages ?? 0,
    currentPage: data.currentPage ?? 1,
    pageSize: data.pageSize ?? 0,
    content: Array.isArray(data.content) ? data.content : [],
  };
};

export const getDrivers = async () => {
  const response = await api.get("/rio/drivers");

  return unwrapList(response);
};

export const getClients = async () => {
  const response = await api.get("/clients");

  return unwrapList(response);
};

export const getVehicles = async () => {
  const response = await api.get("/vehicles");

  return unwrapList(response);
};

type TableDataFilters = {
  branchIds?: string[];
  segmentIds?: string[];
  clientIds?: string[];
  supplierIds?: string[];
  vehicleIds?: string[];
  driverIds?: string[];
  includeNullBranch?: boolean;
  includeNullSegment?: boolean;
  includeNullClient?: boolean;
  includeNullSupplier?: boolean;
  includeNullVehicle?: boolean;
  includeNullDriver?: boolean;
};

export const getDRESummary = async (
  startDate: string,
  endDate: string,
  filters?: TableDataFilters,
  searchTerm?: string
) => {
  const body: Record<string, any> = {
    startDate,
    endDate,
    assetTree: buildAssetTreePayload(filters),
  };

  const cleanSearchTerm = searchTerm?.trim();
  if (cleanSearchTerm) body.searchTerm = cleanSearchTerm;

  const response = await api.post(`${v2BaseUrl}/dre/summary`, body);

  return response.data;
};

type TableDataQuery = {
  page?: number;
  size?: number;
  searchTerm?: string;
};

export const getTableData = async (
  startDate: string,
  endDate: string,
  type: string,
  query?: TableDataQuery,
  filters?: TableDataFilters
) => {
  const body: Record<string, any> = {
    type,
    startDate,
    endDate,
    assetTree: buildAssetTreePayload(filters),
  };

  if (typeof query?.page === "number") body.page = query.page;
  if (typeof query?.size === "number") body.size = query.size;

  const searchTerm = query?.searchTerm?.trim();
  if (searchTerm) body.searchTerm = searchTerm;

  const response = await api.post(`${v2BaseUrl}/transactions`, body);

  return response.data;
};

export const deleteTransaction = async (id: string) => {
  const response = await api.delete(`/transactions/${id}`);
  return response.data;
};

export const getRecurrenceTransactions = async (recurrenceId: string) => {
  const response = await api.get(`/transactions/recurrence/${recurrenceId}`);
  return response.data;
};

export const updateRecurrenceGroup = async (recurrenceId: string, payload: IRecurrenceGroupUpdatePayload) => {
  const response = await api.put(`/transactions/recurrence/${recurrenceId}`, payload);
  return response.data;
};

export const deleteRecurrenceGroup = async (recurrenceId: string, payload: IRecurrenceGroupDeletePayload) => {
  const response = await api.delete(`/transactions/recurrence/${recurrenceId}`, { data: payload });
  return response.data;
};

export const getDetailTransaction = async (id: string) => {
  const response = await api.get(`/transactions/${id}`);
  return response.data;
};

export const uploadFile = async (id: string, file: File) => {
  const formData = new FormData();
  formData.append("file", file);

  const response = await api.post(`/transactions/upload`, formData, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
  });

  return response.data;
};

export const getFile = async (transactionId: string) => {
  const response = await api.get(`/transactions/${transactionId}/attachments`);
  return response.data;
};

export const deleteFile = async (transactionId: string) => {
  const response = await api.delete(`/transactions/${transactionId}/attachments`);
  return response.data;
};

export const exportTransactions = async (
  startDate: string,
  endDate: string,
  type: "RECEITA" | "DESPESA",
  format: "xlsx" | "csv" = "xlsx"
): Promise<void> => {
  const payload = {
    startDate,
    endDate,
    type,
  };

  const response = await api.post(`/transaction-exports/${format}`, payload, {
    responseType: "blob",
  });

  const mimeType = format === "xlsx" ? "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" : "text/csv";

  const blob = new Blob([response.data], { type: mimeType });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;

  const typeLabel = type === "RECEITA" ? "RECEITAS" : "DESPESAS";
  link.setAttribute("download", `${typeLabel}_${startDate}_${endDate}.${format}`);

  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

export const getTags = async () => {
  const response = await api.get("https://api.tags.rio.cloud/tags");
  return response.data;
};

export const getAssetRio = async () => {
  const response = await api.get("https://api.assets.rio.cloud/assets?embed=(tags)");
  return response.data;
};

export interface IMarketplaceVehicle {
  id: string;
  rioAssetId: string;
  name: string;
  licensePlate: string;
  brand: string;
  model: string;
  vehicleType: string;
  tankCapacity: number;
  averageConsumption: number;
  year: number;
  active: boolean;
}

export const getMarketplaceVehicles = async (): Promise<IMarketplaceVehicle[]> => {
  const response = await api.get("/vehicles/marketplace");
  return response.data?.data ?? [];
};

export type AddressSuggestion = {
  id?: string;
  label: string;
  address?: string;
  lat?: number;
  lon?: number;
  distanceMeters?: number;
  raw?: any;
};

const ROUTING_AUTOSUGGEST_URL = process.env.REACT_APP_ROUTING_AUTOSUGGEST_URL!;

const DEFAULT_AUTOSUGGEST_COORDS = { lat: -21.93834460684448, lon: -50.51073917343638 };

const parseDistanceMeters = (rawDistance: unknown): number | undefined => {
  if (typeof rawDistance === "number") {
    return rawDistance;
  }

  if (typeof rawDistance === "string" && rawDistance !== "") {
    return Number(rawDistance);
  }

  return undefined;
};

export const getAddressRouting = async (
  text: string,
  coords?: { lat?: number; lon?: number }
): Promise<AddressSuggestion[]> => {
  const lat = coords?.lat ?? DEFAULT_AUTOSUGGEST_COORDS.lat;
  const lon = coords?.lon ?? DEFAULT_AUTOSUGGEST_COORDS.lon;

  const response = await api.get(ROUTING_AUTOSUGGEST_URL, {
    params: { lat, lon, text },
  });

  const payload = response.data;
  const rawItems: any[] = Array.isArray(payload)
    ? payload
    : (payload?.items ?? payload?.suggestions ?? payload?.results ?? payload?.content ?? []);

  return rawItems.map((item: any, index: number) => {
    const label =
      item?.title || item?.label || item?.name || item?.address?.label || item?.address || item?.description || "";
    const position = item?.position || item?.location || item?.coordinates;
    const rawDistance = item?.distance ?? item?.distanceInMeters ?? item?.distance_meters ?? item?.distanceMeters;
    const distanceMeters = parseDistanceMeters(rawDistance);
    return {
      id: item?.id ?? item?.locationId ?? `${label}-${index}`,
      label: typeof label === "string" ? label : String(label),
      address: typeof item?.address === "string" ? item.address : item?.address?.label,
      lat: position?.lat ?? position?.latitude,
      lon: position?.lon ?? position?.lng ?? position?.longitude,
      distanceMeters: Number.isFinite(distanceMeters) ? distanceMeters : undefined,
      raw: item,
    };
  });
};

export const getPaymentMethods = async () => {
  const response = await api.get("/payment-methods?active=true");
  return unwrapList(response);
};

export const getAllPaymentMethods = async () => {
  const response = await api.get("/payment-methods?includeInactive=true");
  return unwrapList(response);
};

export const createPaymentMethod = async (payload: {
  id: null;
  name: string;
  displayOrder: number;
  active: boolean;
  categoryIds: number[];
}) => {
  const response = await api.post("/payment-methods", payload);
  return response.data;
};

export const editPaymentMethod = async (payload: {
  id: number;
  name: string;
  displayOrder: number;
  active: boolean;
  categoryIds: number[];
}) => {
  const response = await api.post("/payment-methods", payload);
  return response.data;
};

export const inactivatePaymentMethod = async (id: number, activate: boolean) => {
  const response = await api.patch(`/payment-methods/${id}/soft-delete`, { activate });
  return response.data;
};
